;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { FileText, PhoneCall } from "lucide-react";
import * as React from "react";
import type { StepOption } from "@/features/etl-automation/components/automation-steps-builder/automation-step-selector";
import { getImageProps } from "@/lib/utils/image";
import {
	type EtlOperation,
	EtlOperationType,
	type EtlPullStepConfig,
	type EtlPushStepConfig,
	type EtlTransformStepConfig,
	ProviderType,
} from "@/types/etl-automation";
import propstrataIcon from "../../../public/android-chrome-192x192.png";
import appFolioLogo from "../../../public/appfolio.png";
import asanaLogo from "../../../public/asana.png";
import gadminLogo from "../../../public/GAdmin.svg";
import gmeliusLogo from "../../../public/gmelius.svg";
import gotoConnectLogo from "../../../public/goto.png";
import opiniionLogo from "../../../public/opiniion.svg";
import podiumLogo from "../../../public/podium.svg";
import quickbooksLogo from "../../../public/quickbooks-2.svg";
import rentengineLogo from "../../../public/rentengine.png";
import rentvineLogo from "../../../public/rentvine.png";
import showMojoLogo from "../../../public/showmojo.png";
import yardiLogo from "../../../public/yardi.svg";
import zoomLogo from "../../../public/zoom.png";

// ============================================================================
// Provider Metadata
// ============================================================================

/** UI-only sentinel for system automations with no real destination provider. */
export const PROPSTRATA_UI_DESTINATION_PROVIDER_ID = "PROPSTRATA";

interface ProviderMetadata {
	displayName: string;
	description: string;
	category: string;
	icon?:
		| React.ReactNode
		| string
		| { src: string; width?: number; height?: number };
}

const PROVIDER_METADATA: Partial<Record<ProviderType, ProviderMetadata>> = {
	[ProviderType.APP_FOOLIO]: {
		category: "Reporting",
		description: "Property management software",
		displayName: "AppFolio Reporting",
		icon: getImageProps(appFolioLogo),
	},
	[ProviderType.APPFOLIO_DATABASE]: {
		category: "Property Management",
		description: "AppFolio database",
		displayName: "AppFolio Database",
		icon: getImageProps(appFolioLogo),
	},
	[ProviderType.ASANA]: {
		category: "Project Management",
		description: "Work management and project tracking",
		displayName: "Asana",
		icon: getImageProps(asanaLogo),
	},
	[ProviderType.QUICKBOOKS]: {
		category: "Accounting",
		description: "Accounting and financial management",
		displayName: "QuickBooks",
		icon: getImageProps(quickbooksLogo),
	},
	[ProviderType.RENTENGINE]: {
		category: "Property Management",
		description: "Rental applications and leasing operations",
		displayName: "RentEngine",
		icon: getImageProps(rentengineLogo),
	},
	[ProviderType.RENTVINE]: {
		category: "Property Management",
		description: "Property management software",
		displayName: "Rentvine",
		icon: getImageProps(rentvineLogo),
	},
	[ProviderType.YARDI]: {
		category: "Property Management",
		description: "Real estate management platform",
		displayName: "Yardi",
		icon: getImageProps(yardiLogo),
	},
	[ProviderType.OPINIION]: {
		category: "Reputation",
		description: "Reputation management platform",
		displayName: "Opiniion",
		icon: getImageProps(opiniionLogo),
	},
	[ProviderType.PODIUM]: {
		category: "Communication",
		description: "Customer messaging platform",
		displayName: "Podium",
		icon: getImageProps(podiumLogo),
	},
	[ProviderType.GADMIN]: {
		category: "Productivity",
		description: "Google Workspace administration",
		displayName: "Google Admin",
		icon: getImageProps(gadminLogo),
	},
	[ProviderType.GMELIUS]: {
		category: "Communication",
		description: "Email collaboration and CRM",
		displayName: "Gmelius",
		icon: getImageProps(gmeliusLogo),
	},
	[ProviderType.GOTO_CONNECT]: {
		category: "Communication",
		description: "GoTo Connect communications platform",
		displayName: "GoTo Connect",
		icon: getImageProps(gotoConnectLogo),
	},
	[ProviderType.CALL_LINKS]: {
		category: "Communication",
		description: "VoIP call management",
		displayName: "Call Links (VoIP)",
		icon: React.createElement(PhoneCall, { className: "size-4" }),
	},
	[ProviderType.SHOWMOJO]: {
		category: "Reporting",
		description: "ShowMojo listing and showing analytics",
		displayName: "ShowMojo",
		icon: getImageProps(showMojoLogo),
	},
	[ProviderType.ZOOM]: {
		category: "Communication",
		description: "Zoom communications platform",
		displayName: "Zoom",
		icon: getImageProps(zoomLogo),
	},
};

/**
 * Map API providerType (e.g. APP_FOOLIO) to ProviderType for metadata lookup.
 */
export const PROVIDER_TYPE_TO_METADATA: Record<string, ProviderType> = {
	APP_FOOLIO: ProviderType.APP_FOOLIO,
	APPFOLIO: ProviderType.APP_FOOLIO,
	APPFOLIO_DATABASE: ProviderType.APPFOLIO_DATABASE,
	APPFOLIO_REPORTING: ProviderType.APP_FOOLIO,
	ASANA: ProviderType.ASANA,
	CALL_LINKS: ProviderType.CALL_LINKS,
	GADMIN: ProviderType.GADMIN,
	GMELIUS: ProviderType.GMELIUS,
	GOTO_CONNECT: ProviderType.GOTO_CONNECT,
	OPINIION: ProviderType.OPINIION,
	PODIUM: ProviderType.PODIUM,
	QUICKBOOKS: ProviderType.QUICKBOOKS,
	RENTENGINE: ProviderType.RENTENGINE,
	RENTVINE: ProviderType.RENTVINE,
	SHOWMOJO: ProviderType.SHOWMOJO,
	YARDI: ProviderType.YARDI,
	ZOOM: ProviderType.ZOOM,
};

/**
 * Get provider metadata (icon, displayName) by provider ID.
 * Single source of truth for provider display across automation card, row, and detail views.
 */
export function getProviderMetadata(providerId: string): {
	category: string;
	displayName: string;
	icon?: ProviderMetadata["icon"];
} {
	if (providerId === PROPSTRATA_UI_DESTINATION_PROVIDER_ID) {
		return {
			category: "Platform",
			displayName: "Propstrata",
			icon: getImageProps(propstrataIcon),
		};
	}

	const providerType = PROVIDER_TYPE_TO_METADATA[providerId];
	const resolved = providerType && PROVIDER_METADATA[providerType];
	return (
		resolved ?? {
			category: "Data Providers",
			displayName: providerId,
			icon: undefined,
		}
	);
}

/**
 * Map API provider items to StepOption[] for the step selector.
 * Passes through isConnected so unconnected providers can show Connect button.
 */
export function mapProviderApiToStepOptions(
	providers: {
		id: string;
		name: string;
		providerType: string;
		isConnected?: boolean;
	}[],
	operationsMetadata?: Record<string, string>,
): StepOption[] {
	return providers.map((provider) => {
		const providerType = PROVIDER_TYPE_TO_METADATA[provider.providerType];
		const resolved = providerType && PROVIDER_METADATA[providerType];

		const metadata = resolved ?? {
			category: "Data Providers",
			description: "",
			displayName: provider.name,
			icon: undefined,
		};

		return {
			category: metadata.category,
			description:
				operationsMetadata?.[provider.providerType] ?? metadata.description,
			icon: metadata.icon,
			id: provider.providerType,
			isConnected: provider.isConnected ?? true,
			label: metadata.displayName,
		};
	});
}

// ============================================================================
// Operation Metadata
// ============================================================================

interface OperationMetadata {
	category: string;
	icon?: React.ReactNode | string;
}

const OPERATION_METADATA: Record<EtlOperationType, OperationMetadata> = {
	[EtlOperationType.APPFOLIO_TO_QB_INVOICE_PDF]: {
		category: "Invoice Processing",
		icon: React.createElement(FileText, { className: "size-4" }),
	},
	[EtlOperationType.QB_TO_APPFOLIO_UPLOAD_PDF]: {
		category: "Document Upload",
		icon: React.createElement(FileText, { className: "size-4" }),
	},
};

// ============================================================================
// Predefined Operations Registry
// ============================================================================

/**
 * Registry of all available ETL operations
 * In production, this would be fetched from an API endpoint
 */
export const ETL_OPERATIONS: EtlOperation[] = [
	{
		description: "Generate QuickBooks invoice PDFs from AppFolio invoices",
		displayName: "Pull invoices",
		isActive: true,
		operationType: EtlOperationType.APPFOLIO_TO_QB_INVOICE_PDF,
		supportedDestinationProviders: [ProviderType.QUICKBOOKS],
		supportedSourceProviders: [ProviderType.APP_FOOLIO],
		supportsMultipleSources: false,
	},
	{
		description: "Upload QuickBooks invoice PDFs",
		displayName: "Generate and upload invoices",
		isActive: true,
		operationType: EtlOperationType.QB_TO_APPFOLIO_UPLOAD_PDF,
		supportedDestinationProviders: [ProviderType.APP_FOOLIO],
		supportedSourceProviders: [ProviderType.QUICKBOOKS],
		supportsMultipleSources: false,
	},
	// Add more operations as needed
];

// ============================================================================
// Business Logic: Cascading Dependencies
// ============================================================================

/**
 * Get available operations based on selected source providers
 */
export function getAvailableOperations(
	sourceProviders: (ProviderType | string)[],
): EtlOperation[] {
	if (sourceProviders.length === 0) {
		return ETL_OPERATIONS.filter((op) => op.isActive);
	}

	return ETL_OPERATIONS.filter((operation) => {
		if (!operation.isActive) return false;

		// Map API providerType to ProviderType for comparison
		const supportsAllSources = sourceProviders.every((provider) => {
			const providerType = PROVIDER_TYPE_TO_METADATA[provider] ?? provider;
			return operation.supportedSourceProviders.includes(
				providerType as ProviderType,
			);
		});

		// If multiple sources selected, operation must support multiple sources
		if (sourceProviders.length > 1) {
			return supportsAllSources && operation.supportsMultipleSources;
		}

		return supportsAllSources;
	});
}

/**
 * Get available destination providers based on:
 * 1. Selected source providers (PULL step)
 * 2. Selected operation (TRANSFORM step)
 */
export function getAvailableDestinationProviders({
	sourceProviders,
	operationTypes,
}: {
	sourceProviders: (ProviderType | string)[];
	operationTypes: EtlOperationType[];
}): (ProviderType | string)[] {
	if (!operationTypes || operationTypes.length === 0) {
		return [];
	}

	// Get all unique destination providers from all selected operations
	const allDestinations = new Set<ProviderType>();

	for (const operationType of operationTypes) {
		const operation = ETL_OPERATIONS.find(
			(op) => op.operationType === operationType && op.isActive,
		);

		if (operation) {
			operation.supportedDestinationProviders.forEach((provider) => {
				allDestinations.add(provider);
			});
		}
	}

	// Filter out source providers from destination options (map API types for comparison)
	const sourceSet = new Set(
		sourceProviders.map(
			(provider) => PROVIDER_TYPE_TO_METADATA[provider] ?? provider,
		),
	);
	return Array.from(allDestinations).filter(
		(provider) => !sourceSet.has(provider),
	);
}

/**
 * Validate if an automation configuration is valid
 */
export function validateAutomationConfiguration({
	pullStep,
	transformStep,
	pushStep,
}: {
	pullStep: EtlPullStepConfig;
	transformStep: EtlTransformStepConfig;
	pushStep: EtlPushStepConfig;
}): { isValid: boolean; errors: string[] } {
	const errors: string[] = [];

	// Validate PULL step
	if (pullStep.sourceProviders.length === 0) {
		errors.push("At least one source provider must be selected");
	}

	// Validate TRANSFORM step
	if (transformStep.operationTypes.length === 0) {
		errors.push("At least one operation must be selected");
	} else {
		const availableOperations = getAvailableOperations(
			pullStep.sourceProviders,
		);
		const availableOperationTypes = availableOperations.map(
			(op) => op.operationType,
		);

		const invalidOperations = transformStep.operationTypes.filter(
			(opType) => !availableOperationTypes.includes(opType),
		);

		if (invalidOperations.length > 0) {
			errors.push(
				`Selected operation(s) are not available for the chosen source providers`,
			);
		}
	}

	// Validate PUSH step
	if (pushStep.destinationProviders.length === 0) {
		errors.push("At least one destination provider must be selected");
	} else {
		const availableDestinations = getAvailableDestinationProviders({
			operationTypes: transformStep.operationTypes,
			sourceProviders: pullStep.sourceProviders,
		});

		const invalidDestinations = pushStep.destinationProviders.filter(
			(provider: ProviderType) => !availableDestinations.includes(provider),
		);

		if (invalidDestinations.length > 0) {
			errors.push(
				`Selected destination provider(s) are not available for the chosen operation(s)`,
			);
		}
	}

	return {
		errors,
		isValid: errors.length === 0,
	};
}

/**
 * Get all available source providers
 * In production, this would filter based on connected providers for the workspace
 */
export function getAvailableSourceProviders(): ProviderType[] {
	return Object.values(ProviderType);
}

/**
 * Get dataset keys available for a provider
 * In production, this would be fetched from an API
 */
export function getAvailableDatasetKeys(provider: ProviderType): string[] {
	const datasetMap: Partial<Record<ProviderType, string[]>> = {
		[ProviderType.APP_FOOLIO]: ["INVOICES", "PROPERTIES", "BILLS"],
		[ProviderType.QUICKBOOKS]: ["INVOICES", "CUSTOMERS", "ITEMS"],
		[ProviderType.YARDI]: ["PROPERTIES", "UNITS", "RESIDENTS"],
		[ProviderType.OPINIION]: [],
		[ProviderType.GADMIN]: [],
		[ProviderType.GMELIUS]: [],
	};

	return datasetMap[provider] ?? [];
}

// ============================================================================
// StepOption Conversion Functions
// ============================================================================

/**
 * Convert ProviderType[] or providerType[] to StepOption[] for source provider selection
 */
export function getSourceProviderOptions({
	providers,
	recentIds = [],
	favoriteIds = [],
}: {
	providers: (ProviderType | string)[];
	recentIds?: string[];
	favoriteIds?: string[];
}): StepOption[] {
	return providers.map((provider) => {
		const providerType =
			PROVIDER_TYPE_TO_METADATA[provider] ?? (provider as ProviderType);
		const metadata = PROVIDER_METADATA[providerType] ?? {
			category: "Data Providers",
			description: "",
			displayName: String(provider),
			icon: undefined,
		};
		return {
			category: metadata.category,
			description: metadata.description,
			icon: metadata.icon,
			id: String(provider),
			isFavorite: favoriteIds.includes(String(provider)),
			isRecent: recentIds.includes(String(provider)),
			label: metadata.displayName,
		};
	});
}

/**
 * Convert EtlOperation[] to StepOption[] for operation selection
 */
export function getOperationOptions({
	operations,
	recentIds = [],
	favoriteIds = [],
}: {
	operations: EtlOperation[];
	recentIds?: string[];
	favoriteIds?: string[];
}): StepOption[] {
	return operations.map((operation) => {
		const metadata = OPERATION_METADATA[operation.operationType];
		return {
			category: metadata.category,
			description: operation.description,
			icon: metadata.icon,
			id: operation.operationType,
			isFavorite: favoriteIds.includes(operation.operationType),
			isRecent: recentIds.includes(operation.operationType),
			label: operation.displayName,
		};
	});
}

/**
 * Convert ProviderType[] or providerType[] to StepOption[] for destination provider selection
 */
export function getDestinationProviderOptions({
	providers,
	recentIds = [],
	favoriteIds = [],
}: {
	providers: (ProviderType | string)[];
	recentIds?: string[];
	favoriteIds?: string[];
}): StepOption[] {
	return providers.map((provider) => {
		const providerType =
			PROVIDER_TYPE_TO_METADATA[provider] ?? (provider as ProviderType);
		const metadata = PROVIDER_METADATA[providerType] ?? {
			category: "Data Providers",
			description: "",
			displayName: String(provider),
			icon: undefined,
		};
		return {
			category: metadata.category,
			description: metadata.description,
			icon: metadata.icon,
			id: String(provider),
			isFavorite: favoriteIds.includes(String(provider)),
			isRecent: recentIds.includes(String(provider)),
			label: metadata.displayName,
		};
	});
}
